import { Fragment, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import {
  Activity, Award, Ban, KanbanSquare, BarChart3, Briefcase, Moon, Search, ShoppingCart, Sun, GraduationCap, Headset, BadgeCheck, BookUser, FileText, Globe2, Trophy, Banknote, BookOpen, Building2, ChevronDown, ClipboardCheck, Copy as CopyIcon, FileClock, Gauge, KeyRound, LayoutGrid,
  LogOut, Megaphone, Menu, Palette, ScrollText, Settings2, ShieldAlert, ShieldCheck, SlidersHorizontal, Store, Users, UsersRound, Wallet, Wrench, Percent, MailCheck,
  Eye,
} from 'lucide-react';
import { ApiError, applyBranding, type PublicTenant } from '@ck/sdk';
import { hasPermission } from '@ck/core';
import { AuthStage, Logo, PasswordInput, useFavicon, Avatar, UpdateNotice } from '@ck/ui';
import { A, api, me, session, setSignedOutHandler, type Staff, type StaffTenant, photoUrl } from './api';
import { Note } from './ui';
import { Accounts, Dashboard, Kyc, Orders, Payouts, Traders } from './pages/Ops';
/** CRM pages load on demand (and are prefetched in the background) so the CRM opens fast. */
const LOADERS: (() => Promise<unknown>)[] = [];
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function lazyNamed(load: () => Promise<Record<string, unknown>>, name: string): (p: any) => JSX.Element {
  if (!LOADERS.includes(load)) LOADERS.push(load);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const C = reactLazy(() => load().then((m) => ({ default: m[name] as RCT<any> })));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (p: any) => <ReactSuspense fallback={<div className="page-load" aria-busy="true"><i /><i /><i /></div>}><C {...p} /></ReactSuspense>;
}
if (typeof window !== 'undefined') setTimeout(() => LOADERS.forEach((f) => { void f().catch(() => undefined); }), 2500);
const L_Growth = () => import('./pages/Growth');
const Offers = lazyNamed(L_Growth, 'Offers');
const Partners = lazyNamed(L_Growth, 'Partners');
const Subscribers = lazyNamed(L_Growth, 'Subscribers');
const Waitlist = lazyNamed(L_Growth, 'Waitlist');
const L_Promotions = () => import('./pages/Promotions');
const Coupons = lazyNamed(L_Promotions, 'Coupons');
const L_Transparency = () => import('./pages/Transparency');
const TransparencyAdmin = lazyNamed(L_Transparency, 'TransparencyAdmin');
const L_Bot = () => import('./pages/Bot');
const BotKnowledge = lazyNamed(L_Bot, 'BotKnowledge');
const L_Releases = () => import('./pages/Releases');
const Releases = lazyNamed(L_Releases, 'Releases');
const L_Admin = () => import('./pages/Admin');
const Audit = lazyNamed(L_Admin, 'Audit');
const FirmSecurity = lazyNamed(L_Admin, 'FirmSecurity');
const Markets = lazyNamed(L_Admin, 'Markets');
const Security = lazyNamed(L_Admin, 'Security');
const Settings = lazyNamed(L_Admin, 'Settings');
const L_Team = () => import('./pages/Team');
const Team = lazyNamed(L_Team, 'Team');
const L_Book = () => import('./pages/Book');
const Book = lazyNamed(L_Book, 'Book');
const L_Profile = () => import('./pages/Profile');
const TraderProfile = lazyNamed(L_Profile, 'TraderProfile');
const Duplicates = lazyNamed(L_Profile, 'Duplicates');
const L_Certificates = () => import('./pages/Certificates');
const Certificates = lazyNamed(L_Certificates, 'Certificates');
const L_Tools = () => import('./pages/Tools');
const DepartmentHome = lazyNamed(L_Tools, 'DepartmentHome');
const MarketDesk = lazyNamed(L_Tools, 'MarketDesk');
const ToolPage = lazyNamed(L_Tools, 'ToolPage');
import { DEPARTMENT_KITS, INTERN_KITS } from './departments';
import { marketTools, STAFF_TOOLS } from './tools';
const L_Interns = () => import('./pages/Interns');
const Interns = lazyNamed(L_Interns, 'Interns');
const MyInternship = lazyNamed(L_Interns, 'MyInternship');
const L_Support = () => import('./pages/Support');
const SupportDesk = lazyNamed(L_Support, 'SupportDesk');
const L_Woo = () => import('./pages/Woo');
const WooCommerce = lazyNamed(L_Woo, 'WooCommerce');
const L_SocialSignin = () => import('./pages/SocialSignin');
const SocialSignin = lazyNamed(L_SocialSignin, 'SocialSignin');
const L_Workspace = () => import('./pages/Workspace');
const Workspace = lazyNamed(L_Workspace, 'Workspace');
const L_Hr = () => import('./pages/Hr');
const Hr = lazyNamed(L_Hr, 'Hr');
const L_OnboardingPacks = () => import('./pages/OnboardingPacks');
const OnboardingPacks = lazyNamed(L_OnboardingPacks, 'OnboardingPacks');
const SignPacks = lazyNamed(L_OnboardingPacks, 'SignPacks');
const L_IbMarketing = () => import('./pages/IbMarketing');
const IbMarketing = lazyNamed(L_IbMarketing, 'IbMarketing');
const L_Bans = () => import('./pages/Bans');
const BanAlerts = lazyNamed(L_Bans, 'BanAlerts');
const Bans = lazyNamed(L_Bans, 'Bans');
const L_Risk = () => import('./pages/Risk');
const FundingReviews = lazyNamed(L_Risk, 'FundingReviews');
const RiskFlags = lazyNamed(L_Risk, 'RiskFlags');
const L_Insights = () => import('./pages/Insights');
const CustomerNotes = lazyNamed(L_Insights, 'CustomerNotes');
const ProductStats = lazyNamed(L_Insights, 'ProductStats');
const Segments = lazyNamed(L_Insights, 'Segments');
import { SupportAlarm } from './SupportAlarm';
const L_Countries = () => import('./pages/Countries');
const Countries = lazyNamed(L_Countries, 'Countries');
import { NotificationBell } from './pages/People';
const L_People = () => import('./pages/People');
const Leaderboard = lazyNamed(L_People, 'Leaderboard');
const Reports = lazyNamed(L_People, 'Reports');
const TeamActivity = lazyNamed(L_People, 'TeamActivity');
import { Plans } from './Plans';
import { Branding } from './Branding';
import { MarketCtx, setOwnerMode, type MarketFilter } from './market';
import { useDataGuard } from './guard';
import { QuickFind, useCrmTheme } from './shell-bits';
import { CountryModal, WorkdayPopups } from './Workday';
import { lazy as reactLazy, Suspense as ReactSuspense, type ComponentType as RCT } from 'react';


type Page = 'dashboard' | 'traders' | 'kyc' | 'orders' | 'accounts' | 'payouts' | 'partners' | 'offers' | 'coupons' | 'subscribers' | 'waitlist' | 'plans' | 'markets' | 'branding'
  | 'settings' | 'staff' | 'audit' | 'security' | 'certificates' | 'duplicates' | 'firm-security' | 'modules' | 'countries' | 'book' | 'reports' | 'leaderboard' | 'activity' | 'signpacks' | 'ib-marketing' | 'dept' | 'interns' | 'internship' | 'support' | 'tool' | 'mdesk' | 'woocommerce' | 'social-signin' | 'workspace' | 'hr' | 'bans' | 'ban-alerts' | 'risk-flags' | 'funding' | 'segments' | 'notes' | 'product-stats' | 'transparency' | 'ai-support' | 'releases';
interface NavItem { id: Page; label: string; icon: ReactNode; perm?: string; count?: 'kycPending' | 'couponRequests' | 'ordersToReview' | 'payoutsToReview' | 'banAlerts' | 'riskFlags' | 'fundingReviews'; param?: string }
interface NavGroup { group: string; icon: ReactNode; items: NavItem[] }
/** The left menu: single links, and groups that open to show their pages (like a file tree). */
const NAV: NavGroup[] = [
  { group: 'Dashboard', icon: <Gauge />, items: [{ id: 'dashboard', label: 'Dashboard', icon: <Gauge />, perm: 'dashboard.view' }] },
  { group: 'My department', icon: <Briefcase />, items: [{ id: 'dept', label: 'My department', icon: <Briefcase /> }] },
  { group: 'Messages', icon: <Headset />, items: [{ id: 'support', label: 'Messages', icon: <Headset />, perm: 'support' }, { id: 'ai-support', label: 'John Doe - AI Support', icon: <Headset />, perm: 'support' }] },
  { group: 'HR', icon: <UsersRound />, items: [
    { id: 'hr', label: 'HR home', icon: <UsersRound />, perm: 'hr.any' },
    { id: 'hr', param: 'pipeline', label: 'Applicant pipeline', icon: <UsersRound />, perm: 'hr.suite' },
    { id: 'hr', param: 'jobs', label: 'Job posts & website', icon: <UsersRound />, perm: 'hr.suite' },
    { id: 'hr', param: 'directory', label: 'People directory', icon: <UsersRound />, perm: 'hr.suite' },
    { id: 'hr', param: 'contracts', label: 'Agreements & e-sign', icon: <UsersRound />, perm: 'hr.suite' },
    { id: 'hr', param: 'packs', label: 'Onboarding packs', icon: <UsersRound />, perm: 'hr.any' },
    { id: 'hr', param: 'leave', label: 'Time off & leave', icon: <UsersRound />, perm: 'hr.suite' },
    { id: 'hr', param: 'payroll', label: 'Payroll runs', icon: <UsersRound />, perm: 'hr.payroll' },
    { id: 'hr', param: 'invoices', label: 'Contractor invoices', icon: <UsersRound />, perm: 'hr.payroll' },
    { id: 'hr', param: 'killswitch', label: 'Access kill-switch', icon: <UsersRound />, perm: 'hr.suite' },
  ] },
  { group: 'Workspace', icon: <KanbanSquare />, items: [{ id: 'workspace', label: 'Workspace', icon: <KanbanSquare /> }] },
  { group: 'Users', icon: <Building2 />, items: [
    { id: 'staff', label: 'All users', icon: <Building2 />, perm: 'staff.manage' },
    { id: 'staff', param: 'new', label: 'Add user', icon: <Building2 />, perm: 'staff.manage' },
    { id: 'staff', param: 'departments', label: 'Roles & permissions', icon: <Building2 />, perm: 'staff.manage' },
    { id: 'audit', param: 'staff', label: 'User logs', icon: <ScrollText />, perm: 'audit.read' },
    { id: 'leaderboard', label: 'Leaderboard', icon: <Trophy /> },
    { id: 'activity', label: 'Team activity', icon: <Activity />, perm: 'team.activity' },
    { id: 'reports', label: 'Reports', icon: <FileText /> },
    { id: 'interns', label: 'Interns', icon: <GraduationCap />, perm: 'interns' },
    { id: 'internship', label: 'My internship', icon: <GraduationCap />, perm: 'internship' },
  ] },
  { group: 'Products', icon: <SlidersHorizontal />, items: [
    { id: 'plans', label: 'All products', icon: <SlidersHorizontal />, perm: 'plans.write' },
    { id: 'plans', param: 'new', label: 'Add product', icon: <SlidersHorizontal />, perm: 'plans.write' },
    { id: 'markets', label: 'Categories (markets)', icon: <Store />, perm: 'markets.write' },
    { id: 'product-stats', label: 'Product stats', icon: <BarChart3 />, perm: 'dashboard.view' },
    { id: 'audit', param: 'plan', label: 'Product logs', icon: <ScrollText />, perm: 'audit.read' },
    { id: 'certificates', label: 'Certificates', icon: <Award />, perm: 'certificates.write' },
  ] },
  { group: 'Transactions', icon: <Banknote />, items: [
    { id: 'orders', param: 'all', label: 'All transactions', icon: <Banknote />, perm: 'orders.review' },
    { id: 'payouts', label: 'Payouts', icon: <Wallet />, perm: 'payouts.any', count: 'payoutsToReview' },
    { id: 'transparency', label: 'Transparency dashboard', icon: <Eye />, perm: 'payouts.review' },
    { id: 'orders', param: 'refunds', label: 'Refunds', icon: <Banknote />, perm: 'orders.review' },
    { id: 'audit', param: 'order', label: 'Transaction logs', icon: <ScrollText />, perm: 'audit.read' },
    { id: 'settings', label: 'Payment methods & routing', icon: <Settings2 />, perm: 'settings.write' },
  ] },
  { group: 'Orders', icon: <ShoppingCart />, items: [
    { id: 'orders', param: 'paid', label: 'All orders', icon: <ShoppingCart />, perm: 'orders.review' },
    { id: 'orders', label: 'Fulfilment (to confirm)', icon: <ShoppingCart />, perm: 'orders.review', count: 'ordersToReview' },
    { id: 'orders', param: 'chargeback', label: 'Returns & chargebacks', icon: <ShoppingCart />, perm: 'orders.review' },
    { id: 'orders', param: 'pending_payment', label: 'Abandoned checkouts', icon: <ShoppingCart />, perm: 'orders.review' },
    { id: 'woocommerce', label: 'WooCommerce shop', icon: <ShoppingCart />, perm: 'settings.write' },
  ] },
  { group: 'Customers', icon: <Users />, items: [
    { id: 'traders', label: 'All customers', icon: <Users />, perm: 'clients.view' },
    { id: 'book', label: 'My book & leads', icon: <BookUser />, perm: 'book' },
    { id: 'segments', label: 'Segments', icon: <Users />, perm: 'clients.view' },
    { id: 'notes', label: 'Customer notes', icon: <FileText />, perm: 'clients.view' },
    { id: 'kyc', label: 'KYC reviews', icon: <ClipboardCheck />, perm: 'kyc.review', count: 'kycPending' },
    { id: 'accounts', label: 'Trading accounts', icon: <LayoutGrid />, perm: 'accounts.view' },
    { id: 'duplicates', label: 'Duplicate checks', icon: <ShieldAlert />, perm: 'risk.view' },
    { id: 'ban-alerts', label: 'Ban-evasion alerts', icon: <ShieldAlert />, perm: 'clients.ban', count: 'banAlerts' },
    { id: 'waitlist', label: 'Waitlist', icon: <FileClock />, perm: 'waitlist.view' },
  ] },
  { group: 'Marketing', icon: <Megaphone />, items: [
    { id: 'offers', label: 'Offers & codes', icon: <Megaphone />, perm: 'offers.write' },
    { id: 'coupons', label: 'Coupons', icon: <Percent />, perm: 'offers.write', count: 'couponRequests' },
    { id: 'releases', label: 'Product updates', icon: <Megaphone />, perm: 'offers.write' },
    { id: 'subscribers', label: 'Subscribers', icon: <MailCheck />, perm: 'waitlist.view' },
    { id: 'partners', label: 'Affiliates & IBs', icon: <UsersRound />, perm: 'partners.view' },
    { id: 'ib-marketing', label: 'IB marketing accounts', icon: <UsersRound /> },
  ] },
  { group: 'Compliance & security', icon: <ShieldCheck />, items: [
    { id: 'risk-flags', label: 'IP & device red flags', icon: <ShieldAlert />, perm: 'risk.view', count: 'riskFlags' },
    { id: 'funding', label: 'Funding approvals', icon: <ShieldCheck />, perm: 'risk.view', count: 'fundingReviews' },
    { id: 'ban-alerts', label: 'Ban-evasion alerts', icon: <ShieldAlert />, perm: 'clients.ban', count: 'banAlerts' },
    { id: 'bans', label: 'Banned clients', icon: <Ban />, perm: 'clients.ban' },
    { id: 'countries', label: 'Countries & compliance', icon: <Globe2 />, perm: 'countries.write' },
    { id: 'firm-security', label: 'Security & maintenance', icon: <ShieldCheck />, perm: 'settings.write' },
    { id: 'audit', label: 'Audit log', icon: <ScrollText />, perm: 'audit.read' },
  ] },
  { group: 'Brand', icon: <Palette />, items: [{ id: 'branding', label: 'Branding', icon: <Palette />, perm: 'branding.write' }, { id: 'social-signin', label: 'Google & Apple sign-in', icon: <KeyRound />, perm: 'settings.write' }] },
];
const ALL_ITEMS = NAV.flatMap((g) => g.items);

export function App() {
  const [ctx, setCtx] = useState<{ staff: Staff; tenant: StaffTenant } | null>(null);
  const [tenant, setTenant] = useState<PublicTenant | null>(null);
  const [boot, setBoot] = useState(true);
  useFavicon(tenant?.branding);
  useEffect(() => {
    setSignedOutHandler(() => setCtx(null));
    (async () => {
      try {
        const t = await api.tenant();
        setTenant(t);
        applyBranding(t.branding); document.title = `${t.branding.name} CRM`;
        if (session.get()) setCtx(await me().catch(() => null));
      } finally { setBoot(false); }
    })();
  }, []);
  if (boot) return <div className="boot"><span className="boot-mark">·</span></div>;
  if (!tenant) return <div className="fatal"><b>The CRM can’t reach the server.</b><span>Check your connection and reload the page.</span></div>;
  if (!ctx) return <Login tenant={tenant} onIn={async () => setCtx(await me())} />;
  const refresh = async () => setCtx(await me());
  const signOut = async () => { await api.post(A('/logout')).catch(() => undefined); session.set(null); setCtx(null); };
  if (ctx.tenant.settings.security?.require2fa && !ctx.staff.totpEnabled) return <Enforce2fa ctx={ctx} refresh={refresh} signOut={signOut} />;
  return <Shell ctx={ctx} refresh={refresh} signOut={signOut} />;
}

function Login({ tenant, onIn }: { tenant: PublicTenant; onIn: () => Promise<void> }) {
  const [f, setF] = useState({ email: '', password: '', code: '' });
  const [needsCode, setNeedsCode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null); setBusy(true);
    try {
      const r = await api.post<{ token: string }>(A('/login'), { email: f.email, password: f.password, code: needsCode ? f.code : undefined });
      session.set(r.token);
      await onIn();
    } catch (x) {
      if (x instanceof ApiError && x.body.needsCode) { if (needsCode) setError(x.message); setNeedsCode(true); }
      else setError(x instanceof Error ? x.message : 'Sign-in failed.');
    } finally { setBusy(false); }
  }
  return (
    <AuthStage branding={tenant.branding} product="CRM" videoUrl={tenant.branding.loginVideoUrl}
      headline="Run the whole firm from one desk."
      sub="Payments, KYC, payouts, risk and every trader across the five CK markets."
      points={['Confirm payments and open accounts', 'Review identity and pay rewards', 'Change rules, offers and branding live']}>
      <h2>{needsCode ? 'Two-step sign-in' : 'Staff sign-in'}</h2>
      <p className="sub">{needsCode ? `Enter the 6-digit code from your authenticator app for ${f.email}.` : 'Use the work email your firm set up for you.'}</p>
      <form className="form" onSubmit={submit}>
        {!needsCode ? (
          <>
            <label>Work email<input type="email" required autoComplete="username" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} autoFocus /></label>
            <label>Password<PasswordInput required autoComplete="current-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></label>
          </>
        ) : (
          <label>Authentication code
            <input className="code-in" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required autoFocus value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.replace(/\D/g, '') })} />
            <small><button type="button" className="link" onClick={() => { setNeedsCode(false); setF({ ...f, code: '' }); }}>Use a different account</button></small>
          </label>
        )}
        {error && <Note kind="err">{error}</Note>}
        <button className="btn primary wide" disabled={busy}>{busy ? 'Signing in…' : needsCode ? 'Verify and sign in' : 'Sign in to CRM'}</button>
      </form>
      <p className="fine">Every sign-in is recorded. Lost your authenticator? Ask the account owner to reset it.</p>
    </AuthStage>
  );
}

function Enforce2fa({ ctx, refresh, signOut }: { ctx: { staff: Staff; tenant: StaffTenant }; refresh: () => Promise<void>; signOut: () => void }) {
  return (
    <AuthStage branding={ctx.tenant.branding} product="CRM" headline="One more step before you start." sub={`${ctx.tenant.legalName} requires two-step sign-in for every staff member.`}>
      <Security staff={ctx.staff} onChange={refresh} compact />
      <p className="fine"><button className="link" onClick={signOut}>Sign out</button></p>
    </AuthStage>
  );
}

interface Counts { kycPending: number; couponRequests?: number; ordersToReview: number; payoutsToReview: number; banAlerts?: number; riskFlags?: number; fundingReviews?: number }

function readRoute(): { page: string; param?: string } {
  const [, page, param] = location.hash.split('/');
  return { page: page ?? '', param };
}

function Shell({ ctx, refresh, signOut }: { ctx: { staff: Staff; tenant: StaffTenant }; refresh: () => Promise<void>; signOut: () => void }) {
  const { staff } = ctx;
  // Only open markets (CK Funded at launch) show anywhere in the CRM. The Markets settings page still lists every market so they can be switched on.
  const tenant = useMemo(() => ({ ...ctx.tenant, markets: ctx.tenant.markets.filter((m) => m.status === 'open') }), [ctx.tenant]);
  setOwnerMode(staff.role === 'owner');
  const guarded = useDataGuard(staff);
  const allowed = (i: NavItem) => !i.perm || (i.perm === 'support' ? staff.role !== 'intern' : i.perm === 'interns' ? (['owner', 'admin', 'hr'].includes(staff.role) || !!staff.isHead || (staff.supervises ?? 0) > 0) : i.perm === 'internship' ? staff.role === 'intern' : i.perm === 'hr.any' ? ['hr.suite', 'hr.payroll'].some((p) => staff.permissions.includes(p)) : i.perm === 'book' ? ['clients.view_own', 'partners.manage', 'clients.assign'].some((p) => staff.permissions.includes(p)) : hasPermission(staff.permissions, i.perm));
  const first = ALL_ITEMS.find(allowed)!.id;
  const valid = (p: string): Page => ((['owner', 'admin'].includes(staff.role) ? ['security', 'tool', 'mdesk', 'signpacks'] : ['security', 'tool', 'signpacks']).includes(p) ? p as Page : (ALL_ITEMS.find((i) => i.id === p && allowed(i))?.id ?? first));
  const canOpen = (p: string) => ALL_ITEMS.some((i) => i.id === p && allowed(i));
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => { try { return new Set(JSON.parse(localStorage.getItem('ck.crm.nav') ?? '[]') as string[]); } catch { return new Set(); } });
  const toggleGroup = (g: string) => setOpenGroups((cur) => { const n = new Set(cur); if (n.has(g)) n.delete(g); else n.add(g); try { localStorage.setItem('ck.crm.nav', JSON.stringify([...n])); } catch { /* ignore */ } return n; });
  const [waitingChats, setWaitingChats] = useState(0);
  const [deptsOpen, setDeptsOpen] = useState(true);
  const [openDept, setOpenDept] = useState<string | null>(null);
  const [desksOpen, setDesksOpen] = useState(true);
  const [openDesk, setOpenDesk] = useState<string | null>(null);
  const [internOpen, setInternOpen] = useState(false);
  const [route, setRoute] = useState<{ page: Page; param?: string }>(() => { const r = readRoute(); return { page: valid(r.page), param: r.param }; });
  const [menu, setMenu] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [counts, setCounts] = useState<Counts | null>(null);
  const [tick, setTick] = useState(0);
  // Staff only see markets that are open (today: CK Funded). Others appear here when the owner opens them in Markets.
  const openMarkets = useMemo(() => { const o = tenant.markets.filter((m) => m.status === 'open'); return o.length ? o : tenant.markets.slice(0, 1); }, [tenant.markets]);
  const single = openMarkets.length === 1 ? openMarkets[0]!.brand : null;
  const [market, setMarketState] = useState<MarketFilter>(() => { if (single) return single; try { const v = localStorage.getItem('ck.crm.market') as MarketFilter; return v && (v === 'all' || openMarkets.some((m) => m.brand === v)) ? v : 'all'; } catch { return 'all'; } });
  const setMarket = (m: MarketFilter) => { setMarketState(m); try { localStorage.setItem('ck.crm.market', m); } catch { /* ignore */ } };
  const bump = () => setTick((t) => t + 1);
  // Choosing anything in the menu closes it on small screens, even when it is the page already open.
  const go = (p: string, param?: string) => { setMenu(false); setRoute({ page: valid(p), param }); };
  const menuRef = useRef<HTMLDivElement>(null);
  const [theme, setTheme] = useCrmTheme();
  /** Market back offices (the market switch and market desks) are for the owner and admins only. */
  const isBoss = ['owner', 'admin'].includes(staff.role);
  const [askCountry, setAskCountry] = useState(false);
  const [popKey, setPopKey] = useState(0);
  useEffect(() => {
    if (!staff.permissions.includes('support.chat') && !['owner', 'admin'].includes(staff.role)) return;
    const load = () => api.get<{ waiting: number }>(A('/support/stats')).then((r) => setWaitingChats(r.waiting)).catch(() => undefined);
    load(); const t = setInterval(load, 20_000); return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const want = `#/${route.page}${route.param ? `/${route.param}` : ''}`;
    if (location.hash !== want) history.pushState(null, '', want);
    setMenu(false); window.scrollTo(0, 0);
  }, [route]);
  useEffect(() => {
    const onHash = () => { const r = readRoute(); setRoute({ page: valid(r.page), param: r.param }); };
    window.addEventListener('popstate', onHash);
    return () => window.removeEventListener('popstate', onHash);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!staff.permissions.includes('dashboard.view')) return;
    api.get<Counts>(A('/dashboard')).then(setCounts).catch(() => undefined);
  }, [route.page, tick, staff.permissions]);
  useEffect(() => {
    if (!userMenu) return;
    const close = (e: MouseEvent) => { if (!menuRef.current?.contains(e.target as Node)) setUserMenu(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [userMenu]);

  const b = tenant.branding;
  const initials = staff.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  const page = route.page;
  const pageTitle = page === 'dept' ? 'Department' : page === 'tool' ? 'Tool' : page === 'mdesk' ? 'Market desk' : page === 'security' ? 'My security' : page === 'woocommerce' ? 'WooCommerce' : (ALL_ITEMS.find((i) => i.id === page)?.label ?? 'CRM');
  /** Owner and admin: every department and every tool, one click away. */
  const deptsBlock = ['owner', 'admin'].includes(staff.role) ? (
            <>

              <div className="side-g">
                <button className="side-h side-toggle" aria-expanded={deptsOpen} onClick={() => setDeptsOpen(!deptsOpen)}>All departments ({DEPARTMENT_KITS.length}) <ChevronDown className={deptsOpen ? 'up' : ''} /></button>
                {deptsOpen && DEPARTMENT_KITS.map((k) => {
                  const on = page === 'dept' && route.param === k.id;
                  const expanded = openDept === k.id;
                  return (
                    <div key={k.id}>
                      <a href={`#/dept/${k.id}`} className={`side-a${on ? ' on' : ''}`} aria-expanded={expanded} onClick={(e) => { e.preventDefault(); setOpenDept(expanded ? null : k.id); go('dept', k.id); }}>
                        <span className="ic" aria-hidden="true"><Building2 /></span><span className="side-l">{k.name}</span><ChevronDown className={`side-chev${expanded ? ' up' : ''}`} />
                      </a>
                      {expanded && <div className="side-sub">{STAFF_TOOLS.filter((t) => t.group === k.id).map((t) => (
                        <a key={t.id} href={`#/tool/${t.id}`} className={`side-s${page === 'tool' && route.param?.split('?')[0] === t.id ? ' on' : ''}`} onClick={(e) => { e.preventDefault(); if (t.page && canOpen(t.page)) go(t.page); else go('tool', t.id); }}>{t.name}</a>
                      ))}</div>}
                    </div>
                  );
                })}
              </div>
              <div className="side-g">
                <button className="side-h side-toggle" aria-expanded={desksOpen} onClick={() => setDesksOpen(!desksOpen)}>All market desks ({tenant.markets.length}) <ChevronDown className={desksOpen ? 'up' : ''} /></button>
                {desksOpen && tenant.markets.map((m) => {
                  const groups = [...new Map(marketTools(m.brand).map((t) => [t.group, t.groupName])).entries()];
                  const expanded = openDesk === m.brand;
                  return (
                    <div key={m.brand}>
                      <a href={`#/mdesk/${m.brand}`} className={`side-a${page === 'mdesk' && route.param?.split(':')[0] === m.brand ? ' on' : ''}`} aria-expanded={expanded} onClick={(e) => { e.preventDefault(); setOpenDesk(expanded ? null : m.brand); go('mdesk', m.brand); }}>
                        <span className="ic" aria-hidden="true"><Store /></span><span className="side-l">{m.name}</span><ChevronDown className={`side-chev${expanded ? ' up' : ''}`} />
                      </a>
                      {expanded && <div className="side-sub">{groups.map(([gid, gname]) => (
                        <a key={gid} href={`#/mdesk/${m.brand}:${gid}`} className={`side-s${page === 'mdesk' && route.param === `${m.brand}:${gid}` ? ' on' : ''}`} onClick={(e) => { e.preventDefault(); go('mdesk', `${m.brand}:${gid}`); }}>{gname}</a>
                      ))}</div>}
                    </div>
                  );
                })}
              </div>
              <div className="side-g">
                <button className="side-h side-toggle" aria-expanded={internOpen} onClick={() => setInternOpen(!internOpen)}>Intern tools ({INTERN_KITS.length} departments) <ChevronDown className={internOpen ? 'up' : ''} /></button>
                {internOpen && INTERN_KITS.map((k) => (
                  <a key={k.id} href={`#/dept/${k.id}`} className="side-a" onClick={(e) => { e.preventDefault(); go('dept', `${k.id}~intern`); }}><span className="ic" aria-hidden="true"><GraduationCap /></span><span className="side-l">{k.name}</span></a>
                ))}
              </div>
            </>
  ) : null;
  return (
    <MarketCtx.Provider value={{ market, setMarket, markets: openMarkets }}>
      <div className={`shell${menu ? ' menu-open' : ''}`} style={market !== 'all' ? { ['--mk' as string]: tenant.markets.find((m) => m.brand === market)?.accent } : undefined}>
        <aside className="side" aria-label="CRM menu">
          <a className="side-brand" href="#/dashboard" onClick={(e) => { e.preventDefault(); go('dashboard'); }}>
            <Logo branding={b} on={theme === 'dark' ? 'dark' : 'light'} height={26} /><small>CRM</small>
          </a>
          <nav className="side-scroll">
            {!staff.totpEnabled && <button className="side-warn" onClick={() => go('security')}><KeyRound />Turn on two-step sign-in to protect this account</button>}
            {NAV.map((g) => {
              const items = g.items.filter(allowed);
              if (!items.length) return null;
              const isOn = (i: NavItem) => page === i.id && (g.items.some((x) => x.id === i.id && x.param) ? (i.param ?? '') === (route.param ?? '') : true);
              const count = (i: NavItem) => (i.count && counts ? counts[i.count] ?? 0 : 0);
              const total = items.reduce((n, i) => n + count(i), 0) + (g.group === 'Messages' ? waitingChats : 0);
              const single = g.items.length === 1;
              const open = openGroups.has(g.group) || items.some(isOn);
              return (
                <Fragment key={g.group}>
                  {single ? (
                    <a href={`#/${items[0]!.id}`} className={`side-a${isOn(items[0]!) ? ' on' : ''}`} aria-current={isOn(items[0]!) ? 'page' : undefined} onClick={(e) => { e.preventDefault(); go(items[0]!.id); }}>
                      <span className="ic" aria-hidden="true">{g.icon}</span><span className="side-l">{items[0]!.label}</span>{total > 0 && <span className="cnt">{total}</span>}
                    </a>
                  ) : (
                    <div className="side-grp">
                      <button className={`side-a side-gh${items.some(isOn) ? ' has-on' : ''}`} aria-expanded={open} onClick={() => toggleGroup(g.group)}>
                        <span className="ic" aria-hidden="true">{g.icon}</span><span className="side-l">{g.group}</span>{!open && total > 0 && <span className="cnt">{total}</span>}<ChevronDown className={`side-chev${open ? ' up' : ''}`} />
                      </button>
                      {open && <div className="side-sub">{items.map((i) => (
                        <a key={`${i.id}${i.param ?? ''}`} href={`#/${i.id}${i.param ? `/${i.param}` : ''}`} className={`side-s${isOn(i) ? ' on' : ''}`} aria-current={isOn(i) ? 'page' : undefined} onClick={(e) => { e.preventDefault(); go(i.id, i.param); }}>
                          {i.label}{count(i) > 0 && <span className="cnt">{count(i)}</span>}
                        </a>
                      ))}</div>}
                    </div>
                  )}
                </Fragment>
              );
            })}
            {deptsBlock}
            {market !== 'all' && isBoss && (() => {
              const m = tenant.markets.find((x) => x.brand === market);
              const groups = [...new Map(marketTools(market).map((t) => [t.group, t.groupName])).entries()];
              if (!m || !groups.length) return null;
              return (
                <div className="side-g">
                  <span className="side-h">{m.name} desk</span>
                  {groups.map(([gid, gname]) => {
                    const on = page === 'mdesk' && route.param === `${market}:${gid}`;
                    return <a key={gid} href={`#/mdesk/${market}:${gid}`} className={`side-a${on ? ' on' : ''}`} onClick={(e) => { e.preventDefault(); go('mdesk', `${market}:${gid}`); }}><span className="ic" aria-hidden="true"><Store /></span>{gname}</a>;
                  })}
                </div>
              );
            })()}
          </nav>
          <div className="side-foot">
            <div className="side-me"><Avatar src={photoUrl(staff.avatarUrl)} name={staff.name} size={36} /><div><b>{staff.name}</b><small>{staff.department ?? staff.role}</small></div></div>
            <button className="side-out" onClick={signOut}><LogOut />Sign out</button>
          </div>
        </aside>
        {menu && <div className="scrim" onClick={() => setMenu(false)} />}
        {menu && <EscClose close={() => setMenu(false)} />}

        <header className="top">
          <div className="top-in">
            <button className="burger" aria-label="Open menu" aria-expanded={menu} onClick={() => setMenu(!menu)}><Menu /></button>
            <a className="brand" href="#/dashboard" onClick={(e) => { e.preventDefault(); go('dashboard'); }}><Logo branding={b} compact height={30} /></a>
            <h1 className="top-title">{pageTitle}</h1>
            {isBoss && <nav className="switch" aria-label="Market">
              {!single && <button className={`sw sw-all${market === 'all' ? ' on' : ''}`} onClick={() => setMarket('all')}><span className="sw-glyph" />All markets</button>}
              {openMarkets.map((m) => (
                <button key={m.brand} className={`sw${market === m.brand ? ' on' : ''}`} style={{ ['--acc' as string]: m.accent }} onClick={() => setMarket(m.brand)} title={m.status === 'coming_soon' ? `${m.name} is not open yet` : m.name}>
                  <span className="sw-glyph" />{m.name}{m.status === 'coming_soon' && <span className="sw-soon">Soon</span>}
                </button>
              ))}
            </nav>}
            <span className="spacer" />
            <QuickFind staff={staff} allowed={(id) => canOpen(id)} go={go} />
            <button className="avatar icon-b" aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} title={theme === 'dark' ? 'Light mode' : 'Dark mode'} onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>{theme === 'dark' ? <Sun /> : <Moon />}</button>
            <NotificationBell />
            <div className="user" ref={menuRef}>
              <button className="avatar" aria-haspopup="menu" aria-expanded={userMenu} onClick={() => setUserMenu(!userMenu)}>
                <Avatar src={photoUrl(staff.avatarUrl)} name={staff.name} size={28} /><span className="nm">{staff.name.split(' ')[0]}</span><ChevronDown />
              </button>
              {userMenu && (
                <div className="user-menu" role="menu">
                  <div className="um-h"><b>{staff.name}</b><small>{staff.email}, {staff.department ?? staff.role}</small></div>
                  <button role="menuitem" onClick={() => { setUserMenu(false); go('security'); }}><KeyRound />My security</button>
                  <button role="menuitem" onClick={() => { setUserMenu(false); setAskCountry(true); }}><Globe2 />My country & time zone</button>
                  <button role="menuitem" onClick={() => { setUserMenu(false); navigator.clipboard?.writeText(location.href).catch(() => undefined); }}><CopyIcon />Copy link to this page</button>
                  <hr />
                  <button role="menuitem" className="danger" onClick={signOut}><LogOut />Sign out</button>
                </div>
              )}
            </div>
          </div>
        </header>

        <UpdateNotice />
        {staff.permissions.includes('offers.write') && page !== 'releases' && <button className="rel-fab" onClick={() => go('releases')} title="Tell clients about a new update">✨ New update</button>}
        <WorkdayPopups key={popKey} staff={staff} setCountry={() => setAskCountry(true)} />
        {askCountry && <CountryModal onClose={(changed) => { setAskCountry(false); if (changed) setPopKey((k) => k + 1); }} />}
        <SupportAlarm staff={staff} go={go} />
        <main className="main page-in" key={`${page}:${route.param ?? ''}`}>
          {page === 'dashboard' && <Dashboard go={go} tenant={tenant} />}
          {page === 'traders' && (route.param && !route.param.includes('=') ? <TraderProfile id={route.param} staff={staff} tenant={tenant} back={() => go('traders')} /> : <Traders initial={route.param} open={(id) => go('traders', id)} />)}
          {page === 'kyc' && <Kyc onChange={bump} />}
          {page === 'orders' && <Orders onChange={bump} initial={route.param} />}
          {page === 'accounts' && <Accounts tenant={tenant} staff={staff} openTrader={(id) => go('traders', id)} />}
          {page === 'payouts' && <Payouts onChange={bump} staff={staff} />}
          {page === 'duplicates' && <Duplicates open={(id: string) => go('traders', id)} />}
          {page === 'partners' && <Partners staff={staff} tenant={tenant} />}
          {page === 'offers' && <Offers tenant={tenant} />}
          {page === 'waitlist' && <Waitlist tenant={ctx.tenant} />}
          {page === 'coupons' && <Coupons />}
          {page === 'ai-support' && <BotKnowledge />}
          {page === 'releases' && <Releases />}
          {page === 'transparency' && <TransparencyAdmin canEdit={staff.permissions.includes('payouts.review')} canSettings={staff.permissions.includes('settings.write')} />}
          {page === 'subscribers' && <Subscribers staff={staff} />}
          {page === 'plans' && <Plans tenant={tenant} initial={route.param} />}
          {page === 'certificates' && <Certificates staff={staff} tenant={tenant} onChange={refresh} />}
          {page === 'markets' && <Markets tenant={ctx.tenant} onChange={refresh} />}
          {page === 'branding' && <Branding tenant={tenant} onChange={refresh} />}
          {page === 'settings' && <Settings tenant={tenant} onChange={refresh} />}
          {page === 'staff' && <Team staff={staff} tenant={tenant} initial={route.param} />}
          {page === 'book' && <Book staff={staff} open={(id: string) => go('traders', id)} />}
          {page === 'firm-security' && <FirmSecurity tenant={tenant} staff={staff} onChange={refresh} />}
          {page === 'audit' && <Audit area={route.param} />}
          {page === 'countries' && <Countries tenant={tenant} onChange={refresh} />}
          {page === 'reports' && <Reports staff={staff} openId={route.param} go={go} />}
          {page === 'ib-marketing' && <IbMarketing staff={staff} />}
          {page === 'signpacks' && <SignPacks staff={staff} openId={route.param} />}
          {page === 'leaderboard' && <Leaderboard staff={staff} open={(id: string) => go('traders', id)} />}
          {page === 'activity' && <TeamActivity />}
          {page === 'security' && <Security staff={staff} onChange={refresh} />}
          {page === 'woocommerce' && <WooCommerce />}
          {page === 'social-signin' && <SocialSignin />}
          {page === 'workspace' && <Workspace />}
          {page === 'hr' && <Hr staff={staff} param={route.param} go={go} />}
          {page === 'bans' && <Bans go={go} />}
          {page === 'risk-flags' && <RiskFlags go={go} />}
          {page === 'funding' && <FundingReviews go={go} />}
          {page === 'ban-alerts' && <BanAlerts go={go} />}
          {page === 'segments' && <Segments go={go} />}
          {page === 'notes' && <CustomerNotes go={go} />}
          {page === 'product-stats' && <ProductStats go={go} />}
          {page === 'support' && <SupportDesk staff={staff} openId={route.param} go={go} />}
          {page === 'interns' && <Interns staff={staff} openId={route.param} go={go} />}
          {page === 'internship' && <MyInternship go={go} />}
          {page === 'dept' && <DepartmentHome staff={staff} deptId={route.param} go={go} canOpen={canOpen} />}
          {page === 'tool' && route.param && <ToolPage param={route.param} staff={staff} go={go} />}
          {page === 'mdesk' && route.param && <MarketDesk brand={route.param.split(':')[0]!} section={route.param.split(':')[1]} go={go} canOpen={canOpen} />}
          <footer className="foot"><span>{tenant.legalName}</span><span>Every action in the CRM is recorded in the audit log.</span></footer>
        </main>
      </div>
    </MarketCtx.Provider>
  );
}

/** Escape closes the side menu on small screens. */
function EscClose({ close }: { close: () => void }) {
  useEffect(() => { const f = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); }; document.addEventListener('keydown', f); return () => document.removeEventListener('keydown', f); }, [close]);
  return null;
}
